import { useTranslation } from 'react-i18next';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

const FILA = '[data-testid="revisar-fila"]';
const CATEGORIA = '[data-testid="revisar-categoria"], [data-ayuda="categoria"]';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

/** Primera fila de un tipo («normal», «sin-categoria», «traspaso»…). */
function filaDe(tipo: string): Element | null {
  return document.querySelector(`${FILA}[data-ayuda-fila="${tipo}"]`);
}

/** La fila de ejemplo: una normal (con categoría) o, si no hay, la primera. */
function filaEjemplo(): Element | null {
  return filaDe('normal') ?? document.querySelector(FILA);
}

function enFila(fila: Element | null, selector: string): Element[] {
  const el = fila?.querySelector(selector);
  return el ? [el] : [];
}

/** «Sin categoría» que se puede tocar (filas que de verdad la necesitan). */
function sinCategoria(): Element[] {
  return enFila(filaDe('sin-categoria'), '[data-testid="revisar-categoria"]');
}

function chip(tipo: 'sugerencia' | 'ingreso'): Element[] {
  return uno(`[data-testid="revisar-chip"][data-ayuda-chip="${tipo}"]`);
}

/** La categoría de la fila del botón verde de ingreso (para la devolución). */
function categoriaDelIngreso(): Element[] {
  const fila = chip('ingreso')[0]?.closest(FILA) ?? null;
  return fila ? enFila(fila, '[data-testid="revisar-categoria"]') : [];
}

function hayCasilla(): boolean {
  return !!document.querySelector('[data-testid="revisar-casilla"]');
}

function etiquetasFila(t: (texto: string) => string): EtiquetaAyuda[] {
  const fila = filaEjemplo();
  if (!fila) {
    return [];
  }
  const partes: [string, string, boolean][] = [
    ['[data-ayuda="comercio"]', t('Payee'), false],
    [CATEGORIA, t('Category'), true],
    ['[data-ayuda="importe"]', t('Amount'), false],
  ];
  return partes.flatMap(([selector, texto, debajo]) => {
    const el = fila.querySelector(selector);
    return el ? [{ el, texto, debajo }] : [];
  });
}

/**
 * La ayuda de «Por revisar» (maqueta aprobada por Vadik el 27-oct-2026):
 * el recorrido y ocho preguntas. Gavi solo señala; lo único que toca es una
 * casilla, para enseñar la barra de abajo, y la desmarca al salir. Nunca
 * aprueba, categoriza, borra ni abre el detalle. Dos movimientos iguales son
 * dos cobros: nunca enseña a borrar uno por parecer repetido.
 */
export function usePreguntasRevisar(): PreguntaAyuda[] {
  const { t } = useTranslation();

  const enBarra = (
    id: string,
    selector: string,
    texto: string,
    final = false,
  ): PasoAyuda => ({
    id,
    escena: 'revisar-seleccion',
    radio: 12,
    final,
    texto,
    disponible: hayCasilla,
    objetivo: () => uno(selector),
  });
  const pasoFila = (id: string, texto: string, final = false): PasoAyuda => ({
    id,
    escena: 'normal',
    radio: 12,
    final,
    texto,
    objetivo: () => {
      const fila = filaEjemplo();
      return fila ? [fila] : [];
    },
  });

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      radio: 12,
      texto: t(
        'Hi! Here are your new transactions: the ones you have not looked at yet. You check them, give them a category if it is missing and approve them. I will tell you step by step. I look and do not touch, which for a harbour seagull is already a lot.',
      ),
      objetivo: () => uno('[data-ayuda="revisar-contador"]'),
    },
    {
      id: 'todo',
      escena: 'normal',
      radio: 12,
      texto: t(
        'With "Select all" you tick them all at once to approve or categorize them together. Tap it again ("Clear selection") and they are unticked.',
      ),
      objetivo: () => uno('[data-testid="revisar-todo"]'),
    },
    {
      ...pasoFila(
        'fila',
        t(
          'Each row is a new transaction: the payee, its category (or "Uncategorized"), the account and the amount. Green ones with "+" are money coming in.',
        ),
      ),
      etiquetas: () => etiquetasFila(t),
    },
    {
      id: 'casilla',
      escena: 'normal',
      radio: 12,
      texto: t(
        'The box on the left ticks the transaction. A bar then appears at the bottom with what you can do with the ticked ones. We will see it in a moment.',
      ),
      objetivo: () => enFila(filaEjemplo(), '[data-testid="revisar-casilla"]'),
    },
    {
      id: 'sin-categoria',
      escena: 'normal',
      radio: 10,
      texto: t(
        'If it says "Uncategorized", tap it and choose one. Without a category it cannot be approved: that way your Plan adds up. When you choose it, it is approved by itself.',
      ),
      objetivo: sinCategoria,
    },
    {
      id: 'con-categoria',
      escena: 'normal',
      radio: 10,
      texto: t(
        'If it already has a category and it is the right one, you only need to approve it. If not, tap the label and choose another: when you choose it, it is approved too.',
      ),
      objetivo: () =>
        sinCategoria().length > 0
          ? []
          : enFila(filaEjemplo(), '[data-testid="revisar-categoria"]'),
    },
    {
      id: 'sugerencia',
      escena: 'normal',
      radio: 16,
      texto: t(
        'This green one is my bet: you usually put this payee in that category. One tap and it gets that category and is approved. If I am wrong, do not tap it.',
      ),
      objetivo: () => chip('sugerencia'),
    },
    {
      id: 'ingreso',
      escena: 'normal',
      radio: 16,
      texto: t(
        'For money coming in without a category I suggest the income one. One tap and it stays like that and approved. If it is not income, do not tap it.',
      ),
      objetivo: () => (chip('sugerencia').length > 0 ? [] : chip('ingreso')),
    },
    {
      id: 'listos',
      escena: 'normal',
      radio: 14,
      texto: t(
        'This button approves at once all the ones that already have a category, ticked or not. The ones without a category stay in the list waiting.',
      ),
      objetivo: () => uno('[data-testid="revisar-aprobar-listos"]'),
    },
    {
      ...enBarra(
        'barra',
        '[data-testid="revisar-barra"]',
        t(
          'With something ticked this bar appears: how many you have and how much they add up to. "Approve" marks them as fine and they leave this list. Neither amounts nor balances change.',
        ),
      ),
      radio: 18,
    },
    enBarra(
      'categorizar',
      '[data-testid="revisar-categorizar"]',
      t(
        'With "Categorize" you choose one category for all the ticked ones, and they are approved at the same time. "Split" ones are left out: those, one by one.',
      ),
    ),
    enBarra(
      'papelera',
      '[data-testid="revisar-borrar"]',
      t(
        'The bin deletes the ticked ones without asking. If you make a mistake, "Undo" appears at the top for a few seconds. After that, it does not come back.',
      ),
    ),
    pasoFila(
      'abrir',
      t(
        'Tapping the rest of the row opens the whole transaction: there you change the amount or the payee, split it or make it a transfer. Inside there is another porthole.',
      ),
    ),
    {
      id: 'final',
      escena: 'normal',
      radio: 24,
      final: true,
      texto: t(
        'Done! Tap the porthole whenever you want to go over it again. You approve the approving: with my beak I cannot reach the box.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'aprobar',
      texto: t('What is "approving" and why do I have to do it?'),
      pasos: [
        pasoFila(
          'que-es',
          t(
            'Each row is a transaction that came in and you have not looked at yet. Approving is saying "this is fine": you looked at it and it is yours.',
          ),
        ),
        enBarra(
          'saldo',
          '[data-testid="revisar-aprobar"]',
          t(
            'When approved, it leaves this list and is no longer new. It does not change the amount or your balance: it already counted since it arrived.',
          ),
        ),
        {
          id: 'inicio',
          escena: 'normal',
          radio: 20,
          final: true,
          texto: t(
            'When you approve the last one you will see "All reviewed ✓" and go back to where you were. While some are left, this number reminds you on Home.',
          ),
          objetivo: () => uno('[data-testid="insignia-revisar"]'),
        },
      ],
    },
    {
      id: 'no-deja',
      texto: t('Why will it not let me approve?'),
      pasos: [
        {
          id: 'casi-siempre',
          escena: 'normal',
          radio: 10,
          texto: t(
            'It is almost always this: without a category it cannot be approved, so your Plan adds up. Tap "Uncategorized" and choose one. When you choose it, it is approved.',
          ),
          objetivo: sinCategoria,
        },
        {
          id: 'dividido',
          escena: 'normal',
          radio: 12,
          texto: t(
            'A "Split" transaction is approved when all its parts have a category. Tap it to open it and complete them.',
          ),
          objetivo: () => {
            const fila = filaDe('dividido');
            return fila ? [fila] : [];
          },
        },
        enBarra(
          'varios',
          '[data-testid="revisar-aprobar"]',
          t(
            'If you tick several and tap "Approve", the ones with a category are approved and the rest stay ticked. If you ticked only one without a category, the list opens so you can choose it.',
          ),
          true,
        ),
      ],
    },
    {
      id: 'varios',
      texto: t('How do I approve or categorize several at once?'),
      pasos: [
        {
          id: 'marca',
          escena: 'normal',
          radio: 12,
          texto: t(
            'Tick the ones you want with the box, or tap "Select all". At the bottom you will see how many you have and how much they add up to.',
          ),
          objetivo: () => uno('[data-testid="revisar-todo"]'),
        },
        enBarra(
          'categorizar',
          '[data-testid="revisar-categorizar"]',
          t(
            'Tap "Categorize" and choose one: it is given to all the ticked ones and they are approved. "Split" ones do not go in; those, one by one.',
          ),
        ),
        {
          id: 'listos',
          escena: 'normal',
          radio: 14,
          final: true,
          texto: t(
            'And this button approves at once all the ones that already have a category, ticked or not. The ones without one stay in the list.',
          ),
          objetivo: () => uno('[data-testid="revisar-aprobar-listos"]'),
        },
      ],
    },
    {
      id: 'verde',
      texto: t('What is the green thing with "✓" at the end of a row?'),
      pasos: [
        {
          id: 'apuesta',
          escena: 'normal',
          radio: 16,
          texto: t(
            'It is my bet: when you reviewed this payee before, you usually put it in that category. If I am right, one tap and it is categorized and approved.',
          ),
          objetivo: () => chip('sugerencia'),
        },
        {
          id: 'si-no',
          escena: 'normal',
          radio: 10,
          texto: t(
            'If I am wrong, do not tap the green one: tap "Uncategorized" and choose yourself. Next time I will suggest the one you choose.',
          ),
          objetivo: () => {
            const fila = chip('sugerencia')[0]?.closest(FILA) ?? null;
            return enFila(fila, '[data-testid="revisar-categoria"]');
          },
        },
        {
          id: 'ingreso',
          escena: 'normal',
          radio: 16,
          final: true,
          texto: t(
            'For money coming in without history I suggest the income category. If it is not income, do not tap it and choose yourself.',
          ),
          objetivo: () => chip('ingreso'),
        },
      ],
    },
    {
      id: 'equivoco',
      texto: t('What if I make a mistake?'),
      pasos: [
        enBarra(
          'deshacer',
          '[data-testid="revisar-aprobar"]',
          t(
            'Right after approving, categorizing or deleting, "Undo" appears at the top for a few seconds. Tap it and everything goes back to how it was.',
          ),
        ),
        {
          id: 'despues',
          escena: 'normal',
          radio: 10,
          texto: t(
            'If the notice has gone, no problem: the transaction is still in "Transactions". Tap it there and change the category or whatever you need.',
          ),
          objetivo: () => enFila(filaEjemplo(), CATEGORIA),
        },
        enBarra(
          'borrado',
          '[data-testid="revisar-borrar"]',
          t(
            'The only thing that cannot be recovered once the notice has gone is what you delete with the bin. Before deleting, check the payee and the amount carefully.',
          ),
          true,
        ),
      ],
    },
    {
      id: 'iguales',
      texto: t('What if two identical transactions show up?'),
      pasos: [
        pasoFila(
          'dos-cobros',
          t(
            'Two identical ones are two charges: same payee, same amount, twice. I do not merge or delete any on my own. Approve both.',
          ),
        ),
        pasoFila(
          'comprobar',
          t(
            'If you really think one is not yours, tap it to open it and check it calmly. I never remove one for looking repeated.',
          ),
          true,
        ),
      ],
    },
    {
      id: 'traspaso',
      texto: t('What do I do with a transfer between my accounts?'),
      pasos: [
        {
          id: 'dentro',
          escena: 'normal',
          radio: 12,
          texto: t(
            'This is money moving from one of your accounts to another. If both count in your Plan, it does not need a category: it is neither spending nor income. Tick it and tap "Approve".',
          ),
          objetivo: () => {
            const fila = filaDe('traspaso');
            return fila ? [fila] : [];
          },
        },
        {
          id: 'a-fuera',
          escena: 'normal',
          radio: 12,
          texto: t(
            'If it goes to an account that does not count in your Plan (a deposit, an investment), that money leaves your Plan. Tap "Uncategorized" and give it the category it comes out of, like "Rainy day fund".',
          ),
          objetivo: () => {
            const fila = filaDe('traspaso-fuera');
            return fila ? [fila] : [];
          },
        },
        pasoFila(
          'no-lo-pone',
          t(
            'If a transaction is a transfer and it does not say so, open it by tapping the row and change it to a transfer in there.',
          ),
          true,
        ),
      ],
    },
    {
      id: 'devolucion',
      texto: t('What happens if I get a refund?'),
      pasos: [
        {
          id: 'no-ingreso',
          escena: 'normal',
          radio: 16,
          texto: t(
            'A refund is not income: it is spending coming back. Here, do not tap the green income one.',
          ),
          objetivo: () => chip('ingreso'),
        },
        {
          id: 'la-del-gasto',
          escena: 'normal',
          radio: 10,
          final: true,
          texto: t(
            'Tap "Uncategorized" and choose the category of the spending they are refunding, like "Clothing". That way the money goes back to that category and you can use it again.',
          ),
          objetivo: () => {
            const delIngreso = categoriaDelIngreso();
            return delIngreso.length > 0 ? delIngreso : sinCategoria();
          },
        },
      ],
    },
  ];
}
